import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { QMenuButton, QMenuItem, useFeatureFlag } from "@qualio/ui-components";
import {
  BaseDesignElement,
  DesignElementVersion,
  FeatureFlags,
} from "@design-controls/types";
import { UseQueryResult } from "react-query";
import {
  toDesignElementVersionLabel,
  toDesignElementVersionNumber,
} from "../../domain/designElementVersion";

type Props = {
  designElement: Pick<BaseDesignElement, "version" | "major" | "minor">;
  versions: Pick<
    UseQueryResult<DesignElementVersion[]>,
    "data" | "isLoading" | "isError"
  >;
};

export const DesignElementCompareSelect: React.FC<Props> = ({
  designElement,
  versions,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedItemId, baseVersion } = useParams<{
    selectedItemId: string;
    baseVersion?: string;
  }>();
  const features = useFeatureFlag();
  const compareEnabled = Boolean(
    features[FeatureFlags.VERSION_TRACELINK_UI] &&
      features[FeatureFlags.MAJOR_MINOR_VERSION_UI],
  );

  const items = useMemo(() => versions.data ?? [], [versions.data]);

  const selectable = useMemo(
    () => items.filter((item) => item.version !== designElement.version),
    [items, designElement.version],
  );

  const isComparing = baseVersion !== undefined;

  const compareLabel = useMemo(() => {
    if (isComparing) {
      return searchParams.get("version") ?? undefined;
    }

    const viewed = items.find(
      (item) => item.version === designElement.version && !item.isLatest,
    );
    return viewed ? toDesignElementVersionLabel(viewed) : undefined;
  }, [isComparing, searchParams, items, designElement.version]);

  const onSelect = useCallback(
    (label: string) => {
      const elementPath = pathname.slice(
        0,
        pathname.indexOf(`/${selectedItemId}`) + `/${selectedItemId}`.length,
      );

      navigate(
        `${elementPath}/compare/${label}` +
          (compareLabel ? `?version=${compareLabel}` : ""),
      );
    },
    [navigate, pathname, selectedItemId, compareLabel],
  );

  if (!compareEnabled) {
    return null;
  }

  return (
    <QMenuButton
      buttonLabel={
        isComparing
          ? t("designElementCompare.comparingLabel", {
              version: toDesignElementVersionNumber(baseVersion),
            })
          : t("designElementCompare.label")
      }
      variant="ghost"
      isDisabled={versions.isLoading || versions.isError || !selectable.length}
      data-cy="design-element-compare-select"
    >
      {selectable.map((item) => {
        const label = toDesignElementVersionLabel(item);
        const isCurrent = label === baseVersion;
        return (
          <QMenuItem
            key={item.version}
            onClick={() => onSelect(label)}
            bg={isCurrent ? "gray.100" : undefined}
            aria-current={isCurrent ? "true" : undefined}
            data-cy={`design-element-compare-${label}`}
          >
            {t("designElementVersions.label", {
              version: toDesignElementVersionNumber(label),
            })}
          </QMenuItem>
        );
      })}
    </QMenuButton>
  );
};
